<template>
  <div>
    <v-card :elevation="showDetails ? `1` : `0`" v-bind="$attrs" class="contact-tile-card">
      <v-card-text class="mb-0 px-0">
        <v-list-item three-line>
          <v-list-item-avatar v-if="$vuetify.breakpoint.smAndUp" rounded>
            <img :src="memberAvatar" alt="avatar">
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="white-space--normal" v-html="memberName" />
            <v-list-item-subtitle v-if="memberCompany" class="member-company">
              {{ memberCompany }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>{{ contact.title }}</v-list-item-subtitle>
            <v-list-item-subtitle v-if="showClientAccessWarning" class="d-flex align-center flex-wrap gap-2 mt-1">
              <AppTooltip
                v-if="showClientAccessWarning"
                :data-test="`client-access-warning-${clientAccessWarningVariant}`"
                icon="mdi-alert-circle-outline"
                :text="clientAccessWarningText"
                max-width="220px"
              />
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="sourceType === 'company' && memberRoles.length" class="mt-3">
              <CommonChip v-for="role in getContactRoles(memberRoles)" :key="role.text" class="ma-1">
                {{ role.text }}
              </CommonChip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="contact.active === false" class="mb-1">
              <CommonChip color="black" dark>Inactive</CommonChip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="sourceType === 'deal'" class="d-flex align-center flex-wrap gap-2">
              <CommonChip v-if="contact.referring_agent" dense x-small>{{ $t('referring') }}</CommonChip>
              <CommonChip v-if="contact.receiving_agent" dense x-small>{{ $t('receiving') }}</CommonChip>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="expandable">
            <v-btn icon @click="showDetails = !showDetails"><v-icon>{{ showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-card-text>
      <v-divider v-if="showDetails" />
      <v-expand-transition>
        <v-card v-if="showDetails" flat class="mb-5">
          <v-card-text class="pa-0 grey lighten-5">
            <v-card-title class="py-0 pt-3">Contact Details</v-card-title>
            <v-btn-toggle
              v-if="showReferring"
              v-model="referringStatus"
              class="mx-5"
            >
              <v-btn small value="referring">Referring</v-btn>
              <v-btn small value="receiving">Receiving</v-btn>
            </v-btn-toggle>
            <v-list-item v-if="memberPhone" :href="`tel:${memberPhone}`">
              <v-list-item-action>
                <v-icon>mdi-phone</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ memberPhone | formatPhone }}
                  <span v-if="memberPhoneExt" class="ml-1">Ext: {{ memberPhoneExt }}</span>
                </v-list-item-title>
                <v-list-item-subtitle>Direct</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="memberMobile" :href="`tel:${memberMobile}`">
              <v-list-item-action>
                <v-icon>mdi-phone</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ memberMobile | formatPhone }}</v-list-item-title>
                <v-list-item-subtitle>Mobile</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="contact.email" :href="`mailto:${contact.email}`">
              <v-list-item-action>
                <v-icon>mdi-email</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ contact.email }}</v-list-item-title>
                <v-list-item-subtitle>Email</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-card-title v-if="!disableUserActions" class="py-0 mt-3">Actions</v-card-title>
            <v-list-item
              v-if="!disableUserActions"
              :to="getAccountRoute({
                name: 'contact',
                params: { id: contact._key }
              })"
            >
              <v-list-item-action>
                <v-icon>mdi-launch</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>View Contact Profile</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-if="showEditAction && !disableUserActions"
              :class="{ 'filters-action--disabled': isEditDisabled }"
              :title="isEditDisabled ? `${sourceLabel} contacts must be edited at the ${sourceLabel} level` : '' "
              data-track="contact.tile-roles.edit"
              @click="editContactRolesClickHandler"
            >
              <v-list-item-action>
                <v-icon :disabled="isEditDisabled">mdi-pencil</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Edit {{ sourceLabel }} Role(s)</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-if="sourceType === 'deal'"
              :disabled="refreshContactEdge === contact._edge"
              @click="$emit('refresh:contact', contact._edge)"
            >
              <v-list-item-action>
                <v-progress-circular
                  v-if="refreshContactEdge === contact._edge"
                  indeterminate
                  color="primary"
                  size="20"
                  width="1"
                />
                <v-icon v-else>mdi-refresh</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  Refresh Contact Info
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-if="!disableUserActions"
              :class="{ 'filters-action--disabled': isEditDisabled }"
              :title="isEditDisabled ? `${sourceLabel} contacts must be removed at the ${sourceLabel} level` : '' "
              data-track="contact.tile.remove"
              @click="removeContactClickHandler(contact._key)"
            >
              <v-list-item-action>
                <v-icon :disabled="isEditDisabled">mdi-delete</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title v-if="sourceLabel">
                  Remove From {{ sourceLabel }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
          <v-divider />
          <v-card-text class="py-0">
            <v-list-item slot="activator" dense>
              <v-spacer />
              <v-list-item-action class="my-0">
                <v-btn icon @click="showDetails = !showDetails"><v-icon>{{ showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-card-text>
        </v-card>
      </v-expand-transition>
    </v-card>

    <v-dialog v-model="roleDialog" max-width="500">
      <v-form ref="form" @submit.prevent="saveRoles">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>Edit Role(s)</v-toolbar-title>
            <v-spacer />
          </v-toolbar>
          <v-card-text>
            <v-select
              v-model="newRoles"
              :items="contactRoles"
              label="Select the roles for this contact"
              :rules="[(v) => v.length> 0 || 'Contact should have at least one role']"
              multiple
              autocomplete="off"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="primary"
              type="submit"
              :loading="isRolesUpdating"
              data-track="contact.tile-roles.save"
            >
              Save Roles
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { getContactRoles, arrToSnakeCase, ContactRolesEnum } from '@/bundles/Contact/enums/contactRoles';
import { getContactRolesBySource, sourceMappingDictionary } from '@/bundles/Contact/helpers/cards/contactsCard';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';

export default {
  name: 'ContactTile',

  components: {
    CommonChip,
    AppTooltip
  },

  mixins: [AccountLayoutMixin],

  props: {
    contact: {
      type: Object,
      default: () => ({})
    },
    sourceType: {
      type: String,
      default: ''
    },
    contactRole: {
      type: String,
      default: ''
    },
    isRolesUpdating: Boolean,
    refreshContactEdge: {
      type: String,
      default: null
    },
    expandable: Boolean,
    disableUserActions: Boolean,
  },

  data: () => ({
    roleDialog: false,
    newRoles: [],
    showDetails: false,
  }),

  computed: {
    ...mapGetters('Admin', ['getListByCategory']),
    showEditAction () {
      const sources = ['property', 'company', 'availability', 'pipeline', 'occupier', 'availabilityProperty', 'requirement'];
      const sourceType = sourceMappingDictionary[this.sourceType] || this.sourceType;

      return sources.some(source => source === sourceType);
    },
    isEditDisabled () {
      return ['availabilityProperty', 'sign'].includes(this.sourceType);
    },
    sourceLabel () {
      const sources = ['property', 'company', 'availability', 'pipeline', 'occupier', 'requirement', 'deal', 'comparable', 'pipeline_primary_client', 'availabilityProperty', 'sign'];

      const sourceType = sourceMappingDictionary[this.sourceType] || this.sourceType;

      if (sources.some(source => source === sourceType)) {
        return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
      }

      return '';
    },
    contactRoles () {
      return getContactRolesBySource(this.sourceType);
    },
    memberName: function () {
      return this.contact.first_name + ' <strong>' + this.contact.last_name + '</strong>';
    },
    memberCompany: function () {
      return this.contact.company?.name || '';
    },
    memberPhone () {
      return this.contact.phone?.direct || '';
    },
    memberPhoneExt () {
      return this.contact.phone?.direct_ext || '';
    },
    memberMobile () {
      return this.contact.phone?.mobile || '';
    },
    memberAvatar: function () {
      return this.contact.img || this.contact.avatar || this.contactPlaceholder;
    },
    memberRoles () {
      return arrToSnakeCase(this.contact.roles || []);
    },
    hasClientAccessRole () {
      return this.memberRoles.includes(ContactRolesEnum.client_access);
    },
    // These mirror the API's clientAccess.js gates (role + linked `member` + `active`) for a
    // working client login. An archived member has its `member` attribute removed rather than
    // nulled, so both "absent" and "null" mean "no linked member" below. There is no chip here:
    // the Contacts card already groups this contact under its "Client Access" role heading, so a
    // chip would only restate it. Only the failure case is worth surfacing.
    isClientAccessContactActive () {
      return this.contact.active === true;
    },
    hasClientAccessMember () {
      return this.contact.member !== undefined && this.contact.member !== null;
    },
    hasClientAccessLogin () {
      return this.contact.is_client === true && this.isClientAccessContactActive && this.hasClientAccessMember;
    },
    showClientAccessWarning () {
      return this.sourceType === 'occupier' && this.hasClientAccessRole && !this.hasClientAccessLogin;
    },
    // Which warning applies: no member ever linked, vs. a linked member that's now archived.
    // Drives both the message and its data-test hook so the two can't drift apart.
    clientAccessWarningVariant () {
      return (this.contact.is_client === true && this.hasClientAccessMember) ? 'archived' : 'no-login';
    },
    clientAccessWarningText () {
      return this.clientAccessWarningVariant === 'archived'
        ? 'This contact is archived — reactivate it to restore client sign-in.'
        : "No client account linked — this contact can't sign in.";
    },
    showReferring () {
      const allowedRoles = [
        ContactRolesEnum.landlord_agent,
        ContactRolesEnum.buyer_agent,
        ContactRolesEnum.seller_agent,
        ContactRolesEnum.tenant_agent,
        ContactRolesEnum.consulting_agent,
      ]
      return allowedRoles.includes(this.contactRole);
    },
    referringStatus: {
      get () {
        if (this.contact.receiving_agent) {
          return 'receiving';
        }
        if (this.contact.referring_agent) {
          return 'referring';
        }
        return '';
      },
      set (value) {
        const payload = {
          _edge: this.contact._edge,
          contactId: this.contact._key,
          referral: value
        }
        this.$emit('updateReferral', payload)
        return true;
      }
    }
  },

  watch: {
    isRolesUpdating (newVal, oldValue) {
      if (!newVal && oldValue) {
        this.roleDialog = false;
      }
    },
    memberRoles: {
      handler (newVal) {
        this.newRoles = newVal;
      },
      deep: true,
      immediate: true,
    }
  },

  methods: {
    getContactRoles (roles) {
      return getContactRoles(roles);
    },
    saveRoles () {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.$emit('updateRoles', { ...this.contact, roles: this.newRoles });
    },
    editContactRolesClickHandler () {
      if (this.isEditDisabled) {
        return false;
      }

      this.roleDialog = true;
    },
    removeContactClickHandler (key) {
      if (this.isEditDisabled) {
        return false;
      }

      this.$emit('removeContact', key);
    },
  },
}
</script>

<style lang="scss">
.filters-action--disabled {
  color: rgba(0,0,0,.38) !important;
}
.contact-tile-card {
  .v-list-item__subtitle {
    overflow: auto !important;
    -webkit-line-clamp: none !important;
    white-space: normal !important;
  }
}
</style>
